<template>
  <b-modal
    id="cycle-detail-modal"
    v-model="showModal"
    size="lg"
    scrollable
    centered
    hide-footer
    header-class="border-bottom-0 pb-0"
    body-class="pt-1 px-1 px-md-2 pb-2"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
          <b-avatar
            :variant="resolveClassificationVariant(cycle ? cycle.classification : null)"
            size="44px"
            class="mr-1 shadow-sm font-weight-bolder"
          >
            #{{ cycle ? cycle.number : '—' }}
          </b-avatar>
          <div>
            <div
              class="d-flex align-items-center flex-wrap"
              style="gap: 6px;"
            >
              <h5 class="mb-0 font-weight-bolder text-dark">
                {{ $t('Cycle #{number}', { number: cycle ? cycle.number : '' }) }}
              </h5>
              <b-badge
                v-if="cycle && cycle.classification"
                :variant="resolveClassificationVariant(cycle.classification)"
                pill
                class="font-small-1 font-weight-bold"
              >
                {{ resolveClassificationLabel(cycle.classification) }}
              </b-badge>
              <b-badge
                v-if="cycle && cycle.incomplete"
                v-b-tooltip.hover
                variant="light-danger"
                pill
                class="font-small-1 cursor-pointer"
                :title="$t('Incomplete cycle: the period cut the trip, there was a long gap without reading or unloading did not occur')"
              >
                {{ $t('Incomplete') }}
              </b-badge>
              <b-badge
                v-if="cycle && cycle.gpsLost"
                variant="light-warning"
                pill
                class="font-small-1"
              >
                {{ $t('Unstable GPS') }}
              </b-badge>
            </div>
            <small class="text-muted d-block mt-25">
              <feather-icon
                icon="ClockIcon"
                size="12"
                class="mr-25 text-primary"
              />
              {{ cycle ? formatDateTime(cycle.start) : '' }} &nbsp;➔&nbsp; {{ cycle ? formatDateTime(cycle.end) : '' }}
            </small>
          </div>
        </div>

        <b-button
          variant="flat-secondary"
          size="sm"
          class="btn-icon rounded-circle"
          @click="close"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </b-button>
      </div>
    </template>

    <div
      v-if="cycle"
      class="cycle-detail-content"
    >
      <!-- 1. Destaques Executivos de Carga -->
      <b-row class="match-height mb-1">
        <b-col
          cols="6"
          md="3"
          class="mb-50 mb-md-0"
        >
          <div class="p-1 rounded bg-light-primary border border-primary text-center h-100 d-flex flex-column justify-content-center">
            <span class="text-muted font-small-1 font-weight-bold text-uppercase d-flex align-items-center justify-content-center">
              <span>{{ $t('Estimated Load') }}</span>
              <feather-icon
                v-b-tooltip.hover
                icon="HelpCircleIcon"
                size="11"
                class="ml-25 text-muted cursor-pointer"
                :title="$t('The weight of the trip is the stable load on this stretch. Pothole peaks, braking or moving scales are left out, as well as the rise of loading and the descent of unloading.')"
              />
            </span>
            <h3 class="font-weight-bolder text-primary mb-0 mt-25">
              {{ formatNumber(cycle.payloadTonnes) }} <small class="font-small-2">t</small>
            </h3>
            <small class="text-muted font-small-1">{{ $t('Stable load median') }}</small>
          </div>
        </b-col>

        <b-col
          cols="6"
          md="3"
          class="mb-50 mb-md-0"
        >
          <div class="p-1 rounded bg-light-info border border-info text-center h-100 d-flex flex-column justify-content-center">
            <span class="text-muted font-small-1 font-weight-bold text-uppercase">{{ $t('Utilization (%)') }}</span>
            <h3 class="font-weight-bolder text-info mb-0 mt-25">
              <template v-if="cycle.utilizationPercent !== null">
                {{ formatNumber(cycle.utilizationPercent) }}%
              </template>
              <template v-else>
                —
              </template>
            </h3>
            <small class="text-muted font-small-1">
              {{ nominalCapacity ? $t('Capacity: {capacity}t', { capacity: nominalCapacity }) : $t('No nominal capacity') }}
            </small>
          </div>
        </b-col>

        <b-col
          cols="6"
          md="3"
        >
          <div class="p-1 rounded bg-light-success border border-success text-center h-100 d-flex flex-column justify-content-center">
            <span class="text-muted font-small-1 font-weight-bold text-uppercase">{{ $t('Total Duration') }}</span>
            <h3 class="font-weight-bolder text-success mb-0 mt-25">
              {{ formatNumber(cycle.cycleMinutes) }} <small class="font-small-2">min</small>
            </h3>
            <small class="text-muted font-small-1">{{ formatHoursMinutes(cycle.cycleMinutes) }}</small>
          </div>
        </b-col>

        <b-col
          cols="6"
          md="3"
        >
          <div class="p-1 rounded bg-light-secondary border text-center h-100 d-flex flex-column justify-content-center">
            <span class="text-muted font-small-1 font-weight-bold text-uppercase">{{ $t('Loaded Distance') }}</span>
            <h3 class="font-weight-bolder text-dark mb-0 mt-25">
              {{ formatNumber(cycle.loadedDistanceKm) }} <small class="font-small-2">km</small>
            </h3>
            <small class="text-muted font-small-1">{{ $t('Avg. speed') }} {{ formatNumber(cycle.averageSpeedKmh) }} km/h</small>
          </div>
        </b-col>
      </b-row>

      <!-- 2. Linha do Tempo das Fases do Ciclo -->
      <b-card
        no-body
        class="p-1 mb-1 shadow-none border"
      >
        <div class="d-flex align-items-center justify-content-between mb-50">
          <span class="font-weight-bolder font-small-2 text-dark d-flex align-items-center">
            <feather-icon
              icon="LayersIcon"
              size="14"
              class="text-primary mr-50"
            />
            {{ $t('Temporal Composition of the Cycle') }}
          </span>
          <span class="font-small-1 text-muted">
            {{ $t('Total:') }} <strong>{{ formatNumber(cycle.cycleMinutes) }} min</strong>
          </span>
        </div>

        <!-- Barra de Progresso Segmentada -->
        <div
          class="phase-progress-bar mb-75 rounded overflow-hidden d-flex"
          style="height: 12px; background-color: #f1f1f4;"
        >
          <div
            v-if="phasePercentages.loading > 0"
            v-b-tooltip.hover
            class="bg-warning"
            :style="{ width: `${phasePercentages.loading}%` }"
            :title="$t('Loading: {minutes} min ({percent}%)', { minutes: formatNumber(cycle.loadingMinutes), percent: phasePercentages.loading.toFixed(1) })"
          />
          <div
            v-if="phasePercentages.transport > 0"
            v-b-tooltip.hover
            class="bg-primary"
            :style="{ width: `${phasePercentages.transport}%` }"
            :title="$t('Transport: {minutes} min ({percent}%)', { minutes: formatNumber(cycle.transportMinutes), percent: phasePercentages.transport.toFixed(1) })"
          />
          <div
            v-if="phasePercentages.unloading > 0"
            v-b-tooltip.hover
            class="bg-success"
            :style="{ width: `${phasePercentages.unloading}%` }"
            :title="$t('Unload: {minutes} min ({percent}%)', { minutes: formatNumber(cycle.unloadingMinutes), percent: phasePercentages.unloading.toFixed(1) })"
          />
        </div>

        <!-- Fases Detalhadas em Cards/Pills -->
        <b-row class="text-center font-small-2">
          <b-col
            cols="6"
            sm="3"
            class="mb-50 mb-sm-0"
          >
            <div class="p-50 rounded bg-light-warning border border-warning">
              <span class="text-warning font-weight-bold d-block font-small-1">{{ $t('● Loading') }}</span>
              <strong class="font-weight-bolder text-warning">{{ formatNumber(cycle.loadingMinutes) }} min</strong>
              <small class="text-muted d-block font-small-1">{{ $t('Up to 90% charge') }}</small>
            </div>
          </b-col>
          <b-col
            cols="6"
            sm="3"
            class="mb-50 mb-sm-0"
          >
            <div class="p-50 rounded bg-light-primary border border-primary">
              <span class="text-primary font-weight-bold d-block font-small-1">{{ $t('● Transport') }}</span>
              <strong class="font-weight-bolder text-primary">{{ formatNumber(cycle.transportMinutes) }} min</strong>
              <small class="text-muted d-block font-small-1">{{ $t('In transit') }}</small>
            </div>
          </b-col>
          <b-col
            cols="6"
            sm="3"
          >
            <div class="p-50 rounded bg-light-success border border-success">
              <span class="text-success font-weight-bold d-block font-small-1">{{ $t('● Unloading') }}</span>
              <strong class="font-weight-bolder text-success">{{ formatNumber(cycle.unloadingMinutes) }} min</strong>
              <small class="text-muted d-block font-small-1">{{ $t('Dumping/Relief') }}</small>
            </div>
          </b-col>
          <b-col
            cols="6"
            sm="3"
          >
            <div class="p-50 rounded bg-light-secondary border">
              <span
                v-b-tooltip.hover
                class="text-muted font-weight-bold d-block font-small-1 cursor-pointer"
                :title="$t('Empty return: time between the end of one trip and the start of the next')"
              >
                {{ $t('○ Empty Return') }}
              </span>
              <strong class="font-weight-bolder text-dark">
                {{ cycle.emptyReturnMinutes !== null ? `${formatNumber(cycle.emptyReturnMinutes)} min` : $t('Last cycle') }}
              </strong>
              <small class="text-muted d-block font-small-1">{{ $t('Until next start') }}</small>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- 3. Velocidade, Paradas & Dinâmica Operacional -->
      <b-card
        no-body
        class="p-1 mb-1 shadow-none border"
      >
        <h6 class="font-weight-bolder font-small-2 text-dark mb-1 d-flex align-items-center">
          <feather-icon
            icon="NavigationIcon"
            size="14"
            class="text-info mr-50"
          />
          {{ $t('Speeds & Traffic Behavior') }}
        </h6>
        <b-row class="font-small-2">
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Average Speed:') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.averageSpeedKmh) }} km/h</strong>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Maximum Speed:') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.maxSpeedKmh) }} km/h</strong>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Downtime in Transport:') }}</span>
            <div class="d-flex align-items-center">
              <strong :class="cycle.stoppedTransportPercent > 20 ? 'text-danger font-weight-bolder' : 'text-dark font-weight-bolder'">
                {{ formatNumber(cycle.stoppedTransportPercent) }}%
              </strong>
              <feather-icon
                v-if="cycle.stoppedTransportPercent > 20"
                v-b-tooltip.hover
                icon="AlertCircleIcon"
                size="13"
                class="text-danger ml-25"
                :title="$t('More than 20% stopped in transport (possible queue or waiting)')"
              />
            </div>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Post-Cycle Residue:') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.residueTonnes) }} t</strong>
          </b-col>
        </b-row>
      </b-card>

      <!-- 4. Perfil Topográfico & Altimetria -->
      <b-card
        no-body
        class="p-1 mb-1 shadow-none border"
      >
        <h6 class="font-weight-bolder font-small-2 text-dark mb-1 d-flex align-items-center">
          <feather-icon
            icon="TrendingUpIcon"
            size="14"
            class="text-success mr-50"
          />
          {{ $t('Topography & Altimetry of the Route') }}
        </h6>
        <b-row class="font-small-2">
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Initial Altitude (Load):') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.startAltitude) }} m</strong>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Final Altitude (Unload):') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.endAltitude) }} m</strong>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Elevation Change (Ascent):') }}</span>
            <strong :class="cycle.ascentMeters >= 0 ? 'text-primary' : 'text-info'">
              {{ cycle.ascentMeters >= 0 ? `+${formatNumber(cycle.ascentMeters)}` : formatNumber(cycle.ascentMeters) }} m
              <small class="text-muted font-small-1">({{ cycle.ascentMeters >= 0 ? $t('Ascent') : $t('Descent') }})</small>
            </strong>
          </b-col>
          <b-col
            cols="6"
            md="3"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Average Gradient:') }}</span>
            <strong class="text-dark font-weight-bolder">{{ formatNumber(cycle.gradientPercent) }}%</strong>
          </b-col>
        </b-row>
      </b-card>

      <!-- 5. Diagnóstico de Carregamento & Pesagem Dinâmica -->
      <b-card
        no-body
        class="p-1 mb-1 shadow-none border"
      >
        <h6 class="font-weight-bolder font-small-2 text-dark mb-1 d-flex align-items-center">
          <feather-icon
            icon="CpuIcon"
            size="14"
            class="text-warning mr-50"
          />
          {{ $t('Loading & Weighing Diagnostics') }}
        </h6>
        <b-row class="font-small-2">
          <b-col
            cols="12"
            md="4"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Minimum Loader Passes:') }}</span>
            <strong class="text-dark font-weight-bolder">
              {{ cycle.loaderPassesMinimum !== null ? $t('{count} passes (>=5% cap)', { count: cycle.loaderPassesMinimum }) : '—' }}
            </strong>
          </b-col>
          <b-col
            cols="6"
            md="4"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Dynamic Peaks (>125%):') }}</span>
            <strong :class="cycle.dynamicPeakCount > 0 ? 'text-warning' : 'text-muted'">
              {{ $t('{count} sample(s)', { count: cycle.dynamicPeakCount }) }}
            </strong>
          </b-col>
          <b-col
            cols="6"
            md="4"
            class="mb-50"
          >
            <span class="text-muted d-block">{{ $t('Highest Peak Recorded:') }}</span>
            <strong class="text-dark font-weight-bolder">
              {{ cycle.dynamicPeakMaxTonnes !== null ? `${formatNumber(cycle.dynamicPeakMaxTonnes)} t` : $t('None') }}
            </strong>
          </b-col>
        </b-row>
      </b-card>

      <!-- 6. Coordenadas de Carregamento e Descarregamento -->
      <div
        v-if="hasCoordinates"
        class="p-75 rounded bg-light-secondary border font-small-2 d-flex flex-column flex-md-row align-items-md-center justify-content-between"
      >
        <div class="d-flex align-items-center mb-50 mb-md-0">
          <feather-icon
            icon="MapPinIcon"
            size="16"
            class="text-primary mr-50"
          />
          <div>
            <span class="d-block font-weight-bold text-dark">
              {{ $t('Registered Location') }}
            </span>
            <span class="text-muted font-small-1">
              {{ $t('Load:') }} ({{ cycle.loadLat ? cycle.loadLat.toFixed(6) : '—' }}, {{ cycle.loadLng ? cycle.loadLng.toFixed(6) : '—' }}) &nbsp;|&nbsp;
              {{ $t('Unload:') }} ({{ cycle.unloadLat ? cycle.unloadLat.toFixed(6) : '—' }}, {{ cycle.unloadLng ? cycle.unloadLng.toFixed(6) : '—' }})
            </span>
          </div>
        </div>

        <b-button
          v-if="cycle.loadLat && cycle.loadLng"
          variant="outline-primary"
          size="sm"
          target="_blank"
          :href="`https://www.google.com/maps?q=${cycle.loadLat},${cycle.loadLng}`"
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="ExternalLinkIcon"
            size="12"
            class="mr-25"
          />
          <span>{{ $t('View Loading on Map') }}</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { computed } from '@vue/composition-api'
import {
  BModal,
  BRow,
  BCol,
  BCard,
  BBadge,
  BAvatar,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { getIntlLocale } from '@/libs/i18n/dateLocale'

export default {
  name: 'CycleDetailModal',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BModal,
    BRow,
    BCol,
    BCard,
    BBadge,
    BAvatar,
    BButton,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    cycle: {
      type: Object,
      default: null,
    },
    nominalCapacity: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const showModal = computed({
      get: () => props.value,
      set: val => emit('input', val),
    })

    const hasCoordinates = computed(() => (
      props.cycle && ((props.cycle.loadLat && props.cycle.loadLng) || (props.cycle.unloadLat && props.cycle.unloadLng))
    ))

    const phasePercentages = computed(() => {
      if (!props.cycle || !props.cycle.cycleMinutes || props.cycle.cycleMinutes <= 0) {
        return { loading: 0, transport: 0, unloading: 0 }
      }
      const total = props.cycle.cycleMinutes
      const load = Math.max(0, props.cycle.loadingMinutes || 0)
      const trans = Math.max(0, props.cycle.transportMinutes || 0)
      const unload = Math.max(0, props.cycle.unloadingMinutes || 0)

      return {
        loading: Math.min(100, (load / total) * 100),
        transport: Math.min(100, (trans / total) * 100),
        unloading: Math.min(100, (unload / total) * 100),
      }
    })

    const formatNumber = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '—'
      return Number(val).toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })
    }

    const formatDateTime = dateStr => {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return dateStr
      return d.toLocaleString(getIntlLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    }

    const formatHoursMinutes = totalMinutes => {
      if (!totalMinutes || Number.isNaN(totalMinutes)) return ''
      const hrs = Math.floor(totalMinutes / 60)
      const mins = Math.round(totalMinutes % 60)
      if (hrs > 0) return `${hrs}h ${mins}m`
      return `${mins}m`
    }

    const resolveClassificationVariant = classification => {
      if (!classification) return 'light-secondary'
      const s = String(classification).toLowerCase().replace(/[-_\s]/g, '')
      if (s === 'underload' || s === 'subcarga') return 'warning'
      if (s === 'optimal' || s === 'conforme' || s === 'normal') return 'success'
      if (s === 'overload' || s === 'sobrecarga') return 'danger'
      if (s === 'severeoverload' || s === 'sobrecargasevera') return 'dark'
      return 'secondary'
    }

    const resolveClassificationLabel = classification => {
      if (!classification) return '—'
      const s = String(classification).toLowerCase().replace(/[-_\s]/g, '')
      if (s === 'underload' || s === 'subcarga') return root.$t('Underload (<90%)')
      if (s === 'optimal' || s === 'conforme' || s === 'normal') return root.$t('Compliant (90%-110%)')
      if (s === 'overload' || s === 'sobrecarga') return root.$t('Overload (110%-120%)')
      if (s === 'severeoverload' || s === 'sobrecargasevera') return root.$t('Severe Overload (>120%)')
      return classification
    }

    return {
      showModal,
      hasCoordinates,
      phasePercentages,
      formatNumber,
      formatDateTime,
      formatHoursMinutes,
      resolveClassificationVariant,
      resolveClassificationLabel,
    }
  },
}
</script>

<style scoped>
.phase-progress-bar {
  border: 1px solid rgba(0, 0, 0, 0.05);
}
</style>
